import React, { useState } from "react";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import Logo from "../../assets/login/Logo.png";
import BgImage from "../../assets/login/image.JPG";
import { useNavigate } from "react-router-dom";
import { postRequest } from "../../services/AxiosConnection";
import useAuth from "../../hooks/useAuth";
import { useNotification } from "../../context/NotificationContext";
import NotificationSnackbar from "../../utils/NotificationSnackbar";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { triggerNotification } = useNotification();
  const { notification, closeNotification } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await postRequest("/users/login", {
        username,
        password,
      });
      
      const { token, user } = response.data;
      login(token, user);
      
      // Set flag to show sessions popup after navigation
      sessionStorage.setItem("showSessionsPopup", "true");
      sessionStorage.setItem("userId", user.id);
      
      navigate("/dashboard");
      triggerNotification("Successfully logged in!", "success");
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.error("Login failed: Invalid credentials");
        triggerNotification("Invalid credentials. Please try again.", "error");
      } else {
        console.error(
          "Login failed:",
          error.response ? error.response.message : error.message
        );
        triggerNotification("Login failed. Please check your credentials.", "error");
      }
    }
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
      {/* Background layers */}
      <div className="absolute inset-0 bg-[#000c1d]/90" />
      <div
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage: `url(${BgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 md:py-12 lg:py-16 min-h-screen flex items-center justify-center">
        <div className="w-full max-w-5xl bg-transparent">
          <div className="grid md:grid-cols-2 gap-0 h-full">
            {/* Left Side - Welcome Section */}
            <div className="bg-[#0055b8] p-4 sm:p-6 md:p-8 rounded-lg text-white flex flex-col items-center order-2 md:order-1 md:block hidden h-full">
              <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 md:mb-8 text-center">
                Welcome to
              </h2>
              <div className="flex justify-center mb-4 sm:mb-6">
                <a
                  href="https://www.noviusrailtech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={Logo}
                    alt="Smart Coaches Logo"
                    className="w-48 sm:w-56 md:w-64 h-auto"
                  />
                </a>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">
                Novius Hot Axle Box Detection System
              </h1>
              <p className="text-center text-sm sm:text-base mb-8 sm:mb-10 md:mb-12 px-2 sm:px-4">
                Novius Railtech's Hot Axle Box Detection System delivers precise
                thermal alerts, enabling real-time monitoring to prevent axle
                overheating and catastrophic failures.
              </p>
              <div className="mt-auto flex flex-col lg:flex-row gap-2 sm:gap-4 text-xs sm:text-sm text-center justify-center">
                <a
                  href="https://www.noviusrailtech.com/hahw.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline whitespace-nowrap"
                >
                  ABOUT NOVIUS HABD
                </a>
                <span className="hidden sm:hidden lg:inline">|</span>
                <a
                  href="https://noviusrailtech.com/contact.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline whitespace-nowrap"
                >
                  CONTACT US
                </a>
                <span className="hidden sm:hidden lg:inline">|</span>
                <a
                  href="https://noviusrailtech.com/about%20us.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline whitespace-nowrap"
                >
                  ABOUT US
                </a>
              </div>
            </div>
            
            {/* Right Side - Login Form */}
            <div className="bg-transparent p-4 sm:p-6 md:p-8 order-1 md:order-2 backdrop-blur-md bg-white/30 md:bg-transparent flex flex-col items-center justify-center w-full h-full">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8">
                Login to Novius HABD
              </h2>
              <form
                className="space-y-4 sm:space-y-6 w-full max-w-md"
                onSubmit={handleSubmit}
              >
                {/* User ID Input */}
                <div className="relative space-y-2">
                  <label className="text-white text-sm sm:text-base">
                    Username
                  </label>
                  <div className="flex items-center">
                    <FaUser className="absolute left-3 text-gray-500" />
                    <input
                      type="text"
                      className="w-full px-10 sm:px-12 py-2 rounded-md bg-white/90 text-sm sm:text-base"
                      placeholder="Enter Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                {/* Password Input */}
                <div className="relative space-y-2">
                  <label className="text-white text-sm sm:text-base">
                    Password
                  </label>
                  <div className="flex items-center">
                    <FaLock className="absolute left-3 text-gray-500" />
                    <input
                      type={showPassword ? "text" : "password"}
                      className="w-full px-10 sm:px-12 py-2 rounded-md bg-white/90 text-sm sm:text-base"
                      placeholder="Enter Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 text-gray-500"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                
                {/* Remember me checkbox */}
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded"
                  />
                  <label
                    htmlFor="remember"
                    className="text-white text-xs sm:text-sm flex flex-wrap items-center gap-1"
                  >
                    Remember me
                    <a href="#terms" className="text-[#3b82f6] hover:underline">
                      Terms & Conditions
                    </a>
                  </label>
                </div>
                
                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full bg-[#3b82f6] hover:bg-[#2563eb] text-white py-2 sm:py-3 rounded-md font-medium text-sm sm:text-base transition-colors"
                >
                  Sign In
                </button>
                
                {/* Forgot Password & New User Registration Links */}
                <div className="mt-4 text-center">
                  <a
                    href="/forgotpassword"
                    className="text-[#3b82f6] hover:underline text-sm"
                  >
                    Forgot Password?
                  </a>
                  <span className="mx-2 text-white">|</span>
                  <a
                    href="/newuserregistration"
                    className="text-[#3b82f6] hover:underline text-sm"
                  >
                    New User? Register Here
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;